import { interpolateRgb } from 'd3-interpolate'
import { scaleLinear } from 'd3-scale'
import { useMemo } from 'react'

import type { AreaGraphElement, FillFunction, OverlayLine } from '@/features/bidding/components/cascading/charts/types'
import type { DailyForecastPrices, PriceQuantile } from '@/features/bidding/types/forecastPricing'
import { QUANTILE_ORDER } from '@/features/bidding/types/forecastPricing'

// Color scales for value-based coloring (matching hourly bids chart)
const UP_LOW_COLOR = '#f0fdf4' // Light green
const UP_HIGH_COLOR = '#10b981' // Dark green (emerald-500)
const DOWN_LOW_COLOR = '#fef2f2' // Light red
const DOWN_HIGH_COLOR = '#ef4444' // Dark red (red-500)

// Quantile line colors - grey scale
const QUANTILE_LINE_COLORS: Record<PriceQuantile, string> = {
  p10: '#d1d5db', // grey-300
  p25: '#9ca3af', // grey-400
  mean: '#6b7280', // grey-500
  p75: '#9ca3af', // grey-400
  p90: '#d1d5db', // grey-300
}

type UseForecastAreaGraphDataParams = {
  dailyPrices: DailyForecastPrices | null
  selectedQuantile: PriceQuantile
  direction: 'up' | 'down'
}

type UseForecastAreaGraphDataResult = {
  data: AreaGraphElement[]
  datetimes: Date[]
  layerKeys: string[]
  layerFill: FillFunction
  overlayLines: OverlayLine[]
}

/**
 * Transforms forecast data into AreaGraph-compatible format.
 *
 * The selected quantile is used for BOTH:
 * 1. The area fill (via layerData.selectedQuantile) - primary visual representation
 * 2. A highlighted overlay line - for visual emphasis on top of other quantile lines
 */
export function useForecastAreaGraphData({
  dailyPrices,
  selectedQuantile,
  direction,
}: UseForecastAreaGraphDataParams): UseForecastAreaGraphDataResult {
  return useMemo(() => {
    // Default fill function for empty data
    const defaultFill: FillFunction = () => () => (direction === 'up' ? UP_LOW_COLOR : DOWN_LOW_COLOR)

    if (!dailyPrices) {
      return {
        data: [],
        datetimes: [],
        layerKeys: [],
        layerFill: defaultFill,
        overlayLines: [],
      }
    }

    // Create data points for 24 hours
    const data: AreaGraphElement[] = []
    const datetimes: Date[] = []

    // Parse the date string (YYYY-MM-DD) and create proper Date objects for each hour
    const [year, month, day] = dailyPrices.date.split('-').map(Number)

    for (let hour = 0; hour < 24; hour++) {
      const prices = dailyPrices.hourlyPrices.get(hour)
      // Create date in local time (matching how the forecast data is typically displayed)
      const datetime = new Date(year, month - 1, day, hour, 0, 0, 0)

      datetimes.push(datetime)
      data.push({
        datetime,
        meta: { hour, prices },
        layerData: {
          // AREA FILL: Uses the selected quantile's price value
          // This creates the filled area from 0 to the selected quantile price
          selectedQuantile: prices?.[selectedQuantile] ?? 0,
        },
      })
    }

    // Layer keys - the area fill uses the selected quantile
    const layerKeys = ['selectedQuantile']

    // Calculate min/max for value-based coloring
    const values = data.map((d) => d.layerData.selectedQuantile).filter(Number.isFinite)

    // Handle empty values or all-same-value edge cases
    if (values.length === 0) {
      return { data, datetimes, layerKeys, layerFill: defaultFill, overlayLines: [] }
    }

    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)

    // Select colors based on direction
    const lowColor = direction === 'up' ? UP_LOW_COLOR : DOWN_LOW_COLOR
    const highColor = direction === 'up' ? UP_HIGH_COLOR : DOWN_HIGH_COLOR

    // Create color interpolator for value-based coloring
    // Handle edge case where all values are identical (avoid division by zero)
    const domainPadding = minValue === maxValue ? 1 : 0
    const interpolator = interpolateRgb(lowColor, highColor)
    const normalizedScale = scaleLinear()
      .domain([minValue - domainPadding, maxValue + domainPadding])
      .range([0, 1])
      .clamp(true)

    // Layer fill function - returns color based on value (darker = higher price)
    const layerFill: FillFunction = () => (d) => {
      const value = d.data.layerData.selectedQuantile
      if (!Number.isFinite(value)) return lowColor
      const t = normalizedScale(value)
      return interpolator(t)
    }

    // Create overlay lines for ALL quantiles
    const overlayLines: OverlayLine[] = QUANTILE_ORDER.map((q) => ({
      key: q,
      getValue: (d: AreaGraphElement) => {
        const prices = d.meta.prices as Record<PriceQuantile, number> | undefined
        return prices?.[q] ?? 0
      },
      color: QUANTILE_LINE_COLORS[q],
      strokeWidth: 1,
      opacity: 0.6,
    }))

    return { data, datetimes, layerKeys, layerFill, overlayLines }
  }, [dailyPrices, selectedQuantile, direction])
}
